import { Inject, Pipe, PipeTransform } from '@angular/core';
import {
    DateTimeResolvedConfig,
    EndDateDisplayMode,
    RidangoDateInput,
    RidangoDateRange,
    RidangoDateTimeOptions
} from '../../../../core/src/lib/models/ridango-date-time-pipe.interface';
import Env from '../../../../core/src/env';
import { AppStartupService } from '../../../../core/src/lib/services/app-startup.service';
import { DatePipe } from '@angular/common';
import { FormattingContext, OffsetContext } from '../../../../core/src/lib/enums/ridango-date-time-pipe-context.enum';
import { Temporal } from '@js-temporal/polyfill';
import { TranslateService } from '@ngx-translate/core';
import '../../../../../intl.d';

@Pipe({ name: 'ridangoDateTime', pure: false })
export class RidangoDateTimePipe implements PipeTransform {
    constructor(
        @Inject('ENV') private env: Env,
        private appStartupService: AppStartupService,
        private datePipe: DatePipe,
        private translate: TranslateService
    ) {}

    transform(
        value: RidangoDateInput | RidangoDateRange | null | undefined,
        options?: RidangoDateTimeOptions
    ): string | null {
        if (value == null) return null;

        if (Array.isArray(value)) {
            return this.formatRange({ start: value[0], end: value[1] }, options);
        }

        if (value && typeof value === 'object' && 'start' in value && 'end' in value) {
            return this.formatRange(value, options);
        }

        return this.format(value as RidangoDateInput, options);
    }

    format(value: RidangoDateInput, options?: RidangoDateTimeOptions): string {
        if (value == null) return '';

        const zdt = this.resolve(value, options);
        const useFixed = !!this.env?.FORMATTING?.USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE;

        if (useFixed) {
            const pattern = this.env?.PERIOD_TICKET_DATE_FORMAT ?? 'yyyy-MM-dd HH:mm';

            return this.formatWithFixedPattern(this.toDate(zdt), pattern, options?.locale);
        }

        return (
            this.datePipe.transform(this.toDate(zdt), options?.format ?? 'short', undefined, options?.locale ?? null) ??
            ''
        );
    }

    formatRange(range: RidangoDateRange, options?: RidangoDateTimeOptions): string {
        if (!range?.start || !range?.end) {
            return '';
        }

        const startZdt = this.resolve(range.start, options, true);
        const endZdt = this.resolve(range.end, options);
        const useFixed =
            this.env?.FORMATTING?.USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE ??
            this.env?.FORMATTING?.UNIFIED_TICKET_VALIDITY_DISPLAY ??
            false;
        const separator =
            options?.formatOptions?.rangeSeparator ??
            this.env.FORMATTING?.TICKET_VALIDITY_RANGE_SEPARATOR ??
            (useFixed ? ' - ' : ' – ');

        if (useFixed) {
            const pattern = this.env?.PERIOD_TICKET_DATE_FORMAT ?? 'yyyy-MM-dd HH:mm';

            return (
                this.formatWithFixedPattern(this.toDate(startZdt), pattern, options?.locale) +
                separator +
                this.formatWithFixedPattern(this.toDate(endZdt), pattern, options?.locale)
            );
        }

        return `${this.formatZonedDateTime(startZdt, options)}${separator}${this.formatZonedDateTime(endZdt, options)}`;
    }

    resolve(value: RidangoDateInput, options?: RidangoDateTimeOptions, skipMode?: boolean): Temporal.ZonedDateTime {
        if (value == null) {
            throw new Error('RidangoDateTimeFacade: value is required');
        }

        const base = this.buildBaseOptions(options);
        const config = this.resolveConfig(base.context);
        const zdt = this.toZonedDateTime(value, base.timezone);

        if (skipMode) {
            return zdt;
        }

        return this.applyMode(zdt, value, base.timezone, config, base.mode ?? config.mode, options);
    }

    private applyMode(
        zdt: Temporal.ZonedDateTime,
        originalValue: RidangoDateInput,
        timezone: string,
        config: DateTimeResolvedConfig,
        mode: EndDateDisplayMode,
        options?: RidangoDateTimeOptions
    ): Temporal.ZonedDateTime {
        if (config.keepOriginalEndTime) {
            return zdt;
        }

        switch (mode) {
            case 'HYBRID':
                return this.hybrid(zdt, originalValue, timezone, options);
            case 'CALENDAR':
                return this.endOfDay(zdt);
            default:
                return zdt;
        }
    }

    private hybrid(
        zdt: Temporal.ZonedDateTime,
        value: RidangoDateInput,
        timezone: string,
        options?: RidangoDateTimeOptions
    ): Temporal.ZonedDateTime {
        try {
            if (!options?.applyTransportDayOffset) {
                return zdt;
            }

            if (this.shouldSkipHybridOffset(options.productType)) {
                return zdt;
            }

            const instant = this.toInstant(value);
            const offset = this.getTransportationDayOffset(instant);
            const offsetMinutes = offset?.total('minutes') ?? 0;

            if (offsetMinutes <= 0) {
                return zdt;
            }

            const local = instant.toZonedDateTimeISO(timezone);
            const minutes = local.hour * 60 + local.minute;

            if (minutes < offsetMinutes) {
                return this.endOfDay(zdt.subtract({ days: 1 }));
            }

            return zdt;
        } catch {
            return zdt;
        }
    }

    private getTransportationDayOffset(_instant?: Temporal.Instant): Temporal.Duration {
        return this.appStartupService.getTransportationDayOffset();
    }

    private shouldSkipHybridOffset(productType?: string | null): boolean {
        const skipTypes =
            this.env.FORMATTING?.SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET ??
            (this.env.PURCHASE?.SKIP_OFFSET_FOR_DURATION_BASED_TICKETS ? ['M', 'H'] : []);

        return !!(productType && skipTypes.includes(productType));
    }

    private endOfDay(zdt: Temporal.ZonedDateTime): Temporal.ZonedDateTime {
        return zdt.with({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 0
        });
    }

    private formatWithFixedPattern(date: Date, pattern: string, locale?: string | null): string {
        const timezone = this.appStartupService.getTimezone();
        const zdt = Temporal.Instant.fromEpochMilliseconds(date.getTime()).toZonedDateTimeISO(timezone);
        const resolvedLocale = this.resolveDisplayLocale(locale);
        const weekday = zdt.toLocaleString(resolvedLocale, {
            weekday: 'short'
        });
        const capitalizedWeekday = weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : '';

        const { year: y, month: M, day: d, hour: H, minute: m } = zdt;
        const formatted = pattern
            .replace(/yyyy/g, String(y))
            .replace(/MM/g, String(M).padStart(2, '0'))
            .replace(/dd/g, String(d).padStart(2, '0'))
            .replace(/HH/g, String(H).padStart(2, '0'))
            .replace(/mm/g, String(m).padStart(2, '0'));

        return `${capitalizedWeekday} ${formatted}`;
    }

    private resolveDisplayLocale(locale?: string | null): string {
        const normalized =
            this.normalizeLocale(locale) ??
            this.normalizeLocale(this.translate.currentLang) ??
            this.normalizeLocale(this.appStartupService.getLocale()) ??
            'en-US';

        try {
            return new Intl.Locale(normalized).maximize().toString();
        } catch {
            return typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
        }
    }

    private normalizeLocale(langTag?: string | null): string | undefined {
        if (!langTag) {
            return undefined;
        }

        const map: Record<string, string> = {
            se: 'sv-SE',
            sv: 'sv-SE',
            en: 'en-US',
            lv: 'lv-LV',
            ru: 'ru-RU',
            et: 'et-EE',
            lt: 'lt-LT'
        };

        return map[langTag] ?? langTag;
    }

    private toDate(zdt: Temporal.ZonedDateTime): Date {
        return new Date(zdt.epochMilliseconds);
    }

    private buildBaseOptions(options?: RidangoDateTimeOptions): Required<RidangoDateTimeOptions> {
        return {
            format: options?.format ?? 'short',
            timezone: options?.timezone ?? this.appStartupService.getTimezone() ?? 'Etc/UTC',
            locale: options?.locale ?? null,
            fixedEndTime: options?.fixedEndTime,
            context: options?.context,
            mode: options?.mode,
            formatOptions: options?.formatOptions,
            applyTransportDayOffset: options?.applyTransportDayOffset ?? false,
            productType: options?.productType ?? null
        };
    }

    private toZonedDateTime(value: RidangoDateInput, timezone: string): Temporal.ZonedDateTime {
        if (value instanceof Temporal.ZonedDateTime) {
            return value.withTimeZone(timezone);
        }

        if (value instanceof Temporal.PlainDateTime) {
            return value.toZonedDateTime(timezone);
        }

        if (typeof value === 'string') {
            try {
                return Temporal.Instant.from(value).toZonedDateTimeISO(timezone);
            } catch {
                return Temporal.PlainDate.from(value).toZonedDateTime({
                    timeZone: timezone
                });
            }
        }

        return this.toInstant(value).toZonedDateTimeISO(timezone);
    }

    private toInstant(value: RidangoDateInput): Temporal.Instant {
        if (!value) {
            throw new Error('RidangoDateTimeFacade: empty date value');
        }

        if (value instanceof Temporal.Instant) {
            return value;
        }

        if (value instanceof Temporal.ZonedDateTime) {
            return value.toInstant();
        }

        if (value instanceof Temporal.PlainDateTime) {
            return value.toZonedDateTime('UTC').toInstant();
        }

        if (value instanceof Date) {
            return Temporal.Instant.fromEpochMilliseconds(value.getTime());
        }

        if (typeof value === 'number') {
            return Temporal.Instant.fromEpochMilliseconds(value);
        }

        if (typeof value === 'string') {
            return Temporal.Instant.from(value);
        }

        throw new Error(`Unsupported date input: ${Object.prototype.toString.call(value)}`);
    }

    private resolveConfig(context?: FormattingContext | OffsetContext): DateTimeResolvedConfig {
        const formatting = this.env.FORMATTING ?? {};
        const subtractList = (formatting.SUBTRACT_TRANSPORTATION_DAY_OFFSET_FROM as OffsetContext[]) ?? [];
        const offsetContext = this.isOffsetContext(context) ? context : undefined;
        const formatContext = this.isFormattingContext(context) ? context : undefined;
        const formatOptions = formatContext ? formatting[formatContext] : undefined;

        return {
            mode: this.resolveMode(offsetContext, subtractList),
            formatOptions,
            locale: formatOptions?.locale ?? null,
            keepOriginalEndTime: formatting.KEEP_ORIGINAL_ENDING_TIME_FOR_PERIOD_TICKET ?? false
        };
    }

    private resolveMode(context?: OffsetContext, subtractList: OffsetContext[] = []): EndDateDisplayMode {
        const useOffset = this.appStartupService.isTransportationDayOffsetEnabled();

        if ((context && subtractList.includes(context)) || (useOffset && subtractList.length === 0)) {
            return 'HYBRID';
        }

        return 'ACTUAL';
    }

    private isFormattingContext(key: unknown): key is FormattingContext {
        return typeof key === 'string' && Object.values(FormattingContext).includes(key as FormattingContext);
    }

    private isOffsetContext(key: unknown): key is OffsetContext {
        return typeof key === 'string' && Object.values(OffsetContext).includes(key as OffsetContext);
    }

    private formatZonedDateTime(zdt: Temporal.ZonedDateTime, options?: RidangoDateTimeOptions): string {
        return (
            this.datePipe.transform(this.toDate(zdt), options?.format ?? 'short', undefined, options?.locale ?? null) ??
            ''
        );
    }
}
